"use client";

import {
  ArrowRight,
  BadgeCheck,
  Check,
  ChevronRight,
  Clock3,
  FileCheck2,
  Headphones,
  LockKeyhole,
  Menu,
  QrCode,
  Search,
  ShieldCheck,
  Sparkles,
  TicketCheck,
  UploadCloud,
  UserRound,
  X,
} from "lucide-react";
import { FormEvent, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import Swal from "sweetalert2";
import Dashboard, { StaffRole } from "./dashboard";

type PublicView = "inicio" | "solicitud" | "consulta" | "acceso" | "panel";

const safeAlphabet = "23456789ABCDEFGHJKMNPQRSTUVWXYZ";

function randomCode(length: number) {
  return Array.from({ length }, () =>
    safeAlphabet.charAt(Math.floor(Math.random() * safeAlphabet.length)),
  ).join("");
}

const steps = [
  { number: "01", icon: FileCheck2, title: "Registra tu compra", text: "Completa tus datos y adjunta una fotografía clara del comprobante." },
  { number: "02", icon: ShieldCheck, title: "Validamos el pago", text: "Nuestro equipo verifica el monto y confirma que el comprobante sea válido." },
  { number: "03", icon: TicketCheck, title: "Recibe tus boletos", text: "Consulta tus boletos con token único y QR desde un enlace seguro." },
];

const salesPoints = [
  "Gasolinera Next · Av. 27 de Febrero",
  "Estación La Asociación · Santo Domingo Este",
  "Punto móvil · Plaza Central",
  "Otro punto autorizado",
];

export default function RifaApp() {
  const [view, setView] = useState<PublicView>("inicio");
  const [menuOpen, setMenuOpen] = useState(false);
  const [receiptName, setReceiptName] = useState("");
  const [latestRequest, setLatestRequest] = useState("");
  const [trackingCode, setTrackingCode] = useState("");
  const [trackingResult, setTrackingResult] = useState<"idle" | "found" | "missing">("idle");
  const [staffRole, setStaffRole] = useState<StaffRole>("admin");

  const displayTrackingCode = useMemo(() => latestRequest || "SOL-K7M4P9Q8", [latestRequest]);

  function navigate(nextView: PublicView) {
    setView(nextView);
    setMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function submitRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const requestCode = `SOL-${randomCode(8)}`;
    setLatestRequest(requestCode);
    await Swal.fire({
      icon: "success",
      title: "¡Solicitud registrada!",
      html: `Tu código de seguimiento es<br><strong class="swal-code">${requestCode}</strong><br><small>Guárdalo para consultar el estado de tus boletos.</small>`,
      confirmButtonText: "Consultar solicitud",
      confirmButtonColor: "#e81927",
      background: "#111114",
      color: "#ffffff",
      customClass: { popup: "rifa-alert" },
    });
    setTrackingCode(requestCode);
    setTrackingResult("found");
    navigate("consulta");
  }

  function submitTracking(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalized = trackingCode.trim().toUpperCase();
    const knownCodes = [displayTrackingCode, "SOL-T8R3M6K2", "SOL-P7V5H2N9", "SOL-B3N9K5T7", "SOL-M6Q2D8R4"];
    setTrackingResult(knownCodes.includes(normalized) ? "found" : "missing");
  }

  if (view === "panel") {
    return <Dashboard role={staffRole} onRoleChange={setStaffRole} onExit={() => navigate("inicio")} />;
  }

  return (
    <main className="site-shell">
      <header className="public-header">
        <button className="brand" onClick={() => navigate("inicio")} aria-label="Ir al inicio">
          <span className="brand-mark">R</span>
          <span className="brand-copy"><strong>RIFA</strong><small>LA ASOCIACIÓN 2.0</small></span>
        </button>

        <nav className={menuOpen ? "public-nav public-nav-open" : "public-nav"}>
          <button className={view === "inicio" ? "active" : ""} onClick={() => navigate("inicio")}>Inicio</button>
          <button className={view === "solicitud" ? "active" : ""} onClick={() => navigate("solicitud")}>Registrar compra</button>
          <button className={view === "consulta" ? "active" : ""} onClick={() => navigate("consulta")}>Consultar solicitud</button>
          <button className="access-link" onClick={() => navigate("acceso")}><LockKeyhole size={16} /> Acceso interno</button>
        </nav>

        <button className="menu-button" onClick={() => setMenuOpen(!menuOpen)} aria-label="Abrir menú">{menuOpen ? <X /> : <Menu />}</button>
      </header>

      {view === "inicio" && <HomeView onRequest={() => navigate("solicitud")} onTrack={() => navigate("consulta")} />}
      {view === "solicitud" && <RequestView receiptName={receiptName} setReceiptName={setReceiptName} onSubmit={submitRequest} />}
      {view === "consulta" && <TrackingView code={trackingCode} setCode={setTrackingCode} result={trackingResult} onSubmit={submitTracking} demoCode={displayTrackingCode} />}
      {view === "acceso" && <AccessView onLogin={(role) => { setStaffRole(role); navigate("panel"); }} />}

      <footer className="public-footer">
        <div className="brand footer-brand"><span className="brand-mark">R</span><span className="brand-copy"><strong>RIFA</strong><small>LA ASOCIACIÓN 2.0</small></span></div>
        <p>Plataforma oficial de registro y control de boletos.</p>
        <div><ShieldCheck size={16} /> Información protegida</div>
      </footer>
    </main>
  );
}

function HomeView({ onRequest, onTrack }: { onRequest: () => void; onTrack: () => void }) {
  return (
    <>
      <section className="hero-section">
        <div className="hero-glow" />
        <div className="hero-copy">
          <div className="eyebrow"><Sparkles size={15} /> Plataforma oficial de boletos</div>
          <h1>Tu oportunidad<br /><span>empieza aquí.</span></h1>
          <p>Registra tu comprobante, sigue el proceso en línea y recibe tus boletos digitales de forma rápida y segura.</p>
          <div className="hero-actions">
            <button className="primary-button" onClick={onRequest}>Registrar mi compra <ArrowRight size={19} /></button>
            <button className="secondary-button" onClick={onTrack}><Search size={18} /> Consultar solicitud</button>
          </div>
          <div className="trust-row">
            <span><BadgeCheck size={18} /> Validación controlada</span>
            <span><QrCode size={18} /> QR individual</span>
            <span><LockKeyhole size={18} /> Seguimiento seguro</span>
          </div>
        </div>

        <div className="flyer-stage">
          <div className="flyer-frame">
            {/* The uploaded flyer is served directly so the preview and print assets stay pixel-perfect. */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/assets/flyer-rifa-asociacion-2.png" alt="Flyer de la Rifa La Asociación 2.0" width={1024} height={1280} />
            <span className="live-badge"><span /> Rifa activa</span>
          </div>
          <div className="price-card"><small>Valor por boleto</small><strong>RD$ 1,000</strong><span>Precio configurado por la asociación</span></div>
        </div>
      </section>

      <section className="process-section">
        <div className="section-heading"><span>Proceso transparente</span><h2>Tres pasos. Cero complicaciones.</h2><p>Cada solicitud queda vinculada a su comprobante y a los boletos generados.</p></div>
        <div className="steps-grid">
          {steps.map(({ icon: Icon, ...step }) => (
            <article className="step-card" key={step.number}><span className="step-number">{step.number}</span><div className="step-icon"><Icon /></div><h3>{step.title}</h3><p>{step.text}</p><ChevronRight className="step-arrow" /></article>
          ))}
        </div>
      </section>

      <section className="control-banner">
        <div className="control-icon"><ShieldCheck /></div>
        <div><span>Control y trazabilidad</span><h2>Cada boleto tiene una historia verificable.</h2><p>Solicitud, comprobante, validación, token y QR quedan conectados en un solo registro.</p></div>
        <button className="light-button" onClick={onTrack}>Ver estado de mi solicitud <ArrowRight size={18} /></button>
      </section>
    </>
  );
}

function RequestView({ receiptName, setReceiptName, onSubmit }: { receiptName: string; setReceiptName: (value: string) => void; onSubmit: (event: FormEvent<HTMLFormElement>) => void }) {
  return (
    <section className="inner-page request-page">
      <div className="inner-heading"><div className="eyebrow"><TicketCheck size={15} /> Registro de compra</div><h1>Solicita tus boletos</h1><p>Completa la información tal como aparece en tu identificación y adjunta el comprobante recibido en el punto autorizado.</p></div>
      <form className="request-form" onSubmit={onSubmit}>
        <div className="form-section-title"><span>1</span><div><h2>Datos del participante</h2><p>La información se usará para identificar tus boletos.</p></div></div>
        <div className="form-grid">
          <label className="field field-wide"><span>Nombre completo *</span><div className="input-wrap"><UserRound /><input required placeholder="Ej. María Fernanda Rodríguez" /></div></label>
          <label className="field"><span>Tipo de identificación *</span><select required defaultValue="cedula"><option value="cedula">Cédula</option><option value="pasaporte">Pasaporte</option><option value="otro">Otro documento</option></select></label>
          <label className="field"><span>Número de identificación *</span><input required placeholder="000-0000000-0" /></label>
          <label className="field"><span>WhatsApp *</span><div className="phone-input"><select aria-label="Código de país" defaultValue="+1"><option>+1</option><option>+502</option><option>+1 829</option><option>+1 849</option></select><input required type="tel" placeholder="809 555 0124" /></div></label>
          <label className="field"><span>Correo electrónico</span><input type="email" placeholder="correo@ejemplo.com" /></label>
        </div>
        <div className="form-divider" />
        <div className="form-section-title"><span>2</span><div><h2>Información del pago</h2><p>Selecciona dónde compraste y adjunta una imagen legible.</p></div></div>
        <div className="form-grid">
          <label className="field field-wide"><span>Punto de venta autorizado *</span><select required defaultValue=""><option value="" disabled>Selecciona un punto de venta</option>{salesPoints.map((point) => <option key={point}>{point}</option>)}</select></label>
          <label className="field"><span>Monto pagado *</span><div className="money-input"><span>RD$</span><input required type="number" min="1000" step="1000" placeholder="3,000" /></div><small>El sistema calculará los boletos con el monto validado.</small></label>
          <label className="field"><span>Comentario opcional</span><textarea placeholder="Agrega alguna observación para el validador" /></label>
          <label className="upload-field field-wide"><input required type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => setReceiptName(event.target.files?.[0]?.name || "")} /><div className="upload-icon">{receiptName ? <Check /> : <UploadCloud />}</div><strong>{receiptName || "Adjunta el comprobante de pago"}</strong><span>{receiptName ? "Imagen lista para enviar" : "JPG, PNG o WEBP · Máximo 10 MB"}</span><em>{receiptName ? "Cambiar imagen" : "Seleccionar imagen"}</em></label>
        </div>
        <div className="consent-row"><ShieldCheck /><p>Al enviar confirmas que los datos son correctos. La solicitud será revisada antes de generar cualquier boleto.</p></div>
        <button className="primary-button submit-button" type="submit">Enviar solicitud <ArrowRight size={19} /></button>
      </form>
    </section>
  );
}

function TrackingView({ code, setCode, result, onSubmit, demoCode }: { code: string; setCode: (value: string) => void; result: "idle" | "found" | "missing"; onSubmit: (event: FormEvent<HTMLFormElement>) => void; demoCode: string }) {
  return (
    <section className="inner-page tracking-page">
      <div className="inner-heading compact-heading"><div className="eyebrow"><Search size={15} /> Seguimiento en línea</div><h1>Consulta tu solicitud</h1><p>Ingresa el código recibido al registrar tu comprobante.</p></div>
      <form className="tracking-search" onSubmit={onSubmit}><Search /><input value={code} onChange={(event) => setCode(event.target.value.toUpperCase())} placeholder="Ej. SOL-K7M4P9Q8" required /><button type="submit">Consultar <ArrowRight size={18} /></button></form>
      <button className="demo-code" onClick={() => setCode(demoCode)}>Probar con: <strong>{demoCode}</strong></button>
      {result === "missing" && <div className="not-found"><Search /><div><strong>No encontramos esa solicitud</strong><p>Revisa el código e inténtalo nuevamente.</p></div></div>}
      {result === "found" && <TrackingResult code={code || demoCode} />}
      <div className="help-card"><Headphones /><div><strong>¿Necesitas ayuda?</strong><p>Comunícate con el punto de venta donde realizaste la compra.</p></div><button>Contactar soporte</button></div>
    </section>
  );
}

function TrackingResult({ code }: { code: string }) {
  const normalized = code.trim().toUpperCase();
  const generated = normalized === "SOL-P7V5H2N9";
  const rejected = normalized === "SOL-B3N9K5T7";
  const correction = normalized === "SOL-M6Q2D8R4";
  const timeline = [
    { title: "Solicitud recibida", text: "Comprobante registrado correctamente", date: "17 ago · 10:42 a. m.", done: true },
    { title: "Pendiente de revisión", text: "La solicitud ingresó a la cola de validación", date: "17 ago · 10:43 a. m.", done: true },
    {
      title: generated ? "Solicitud aprobada" : rejected ? "Solicitud rechazada" : correction ? "Corrección requerida" : "En proceso",
      text: generated ? "El pago fue validado por RD$ 5,000" : rejected ? "El comprobante ya había sido utilizado" : correction ? "El monto no se distingue en la imagen" : "Un validador está revisando tu comprobante",
      date: generated ? "17 ago · 11:04 a. m." : rejected || correction ? "16 ago · 7:35 p. m." : "En curso",
      done: generated,
    },
    { title: "Boletos generados", text: generated ? "5 boletos disponibles para consultar" : "Los boletos aparecerán aquí al ser aprobados", date: generated ? "17 ago · 11:05 a. m." : "Pendiente", done: generated },
  ];
  const publicStatus = generated ? "Boletos generados" : rejected ? "Rechazada" : correction ? "Corrección requerida" : "En proceso";
  const generatedTokens = ["K7M4P9", "R8W3H6", "C5T9N2", "Y4F8Q7", "D3V6K8"];

  async function downloadPublicTickets() {
    const [{ jsPDF }, QRCode] = await Promise.all([import("jspdf"), import("qrcode")]);
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    pdf.setFillColor(17, 17, 20);
    pdf.rect(0, 0, 210, 297, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(22);
    pdf.text("RIFA LA ASOCIACION 2.0", 18, 20);
    pdf.setTextColor(223, 44, 56);
    pdf.setFontSize(10);
    pdf.text(`SOLICITUD ${normalized}`, 18, 28);
    for (let index = 0; index < generatedTokens.length; index += 1) {
      const token = generatedTokens[index];
      const y = 38 + index * 48;
      pdf.setDrawColor(60, 60, 67);
      pdf.roundedRect(18, y, 174, 40, 2, 2, "S");
      const qr = await QRCode.toDataURL(`https://rifa-asociacion.do/boleto/${token}`, { width: 260, margin: 1, errorCorrectionLevel: "M" });
      pdf.addImage(qr, "PNG", 23, y + 5, 30, 30);
      pdf.setTextColor(140, 140, 150);
      pdf.setFontSize(8);
      pdf.text("TOKEN UNICO", 62, y + 13);
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(21);
      pdf.text(token, 62, y + 25);
      pdf.setTextColor(62, 196, 143);
      pdf.setFontSize(8);
      pdf.text("ACTIVO", 165, y + 22);
    }
    pdf.save(`boletos-${normalized}.pdf`);
  }
  return (
    <article className="tracking-card">
      <div className="tracking-card-head"><div><span>Solicitud</span><h2>{code}</h2></div><span className={`status-pill ${generated ? "status-ready" : rejected ? "status-rejected" : correction ? "status-correction" : "status-process"}`}>{generated ? <TicketCheck /> : rejected ? <X /> : correction ? <FileCheck2 /> : <Clock3 />} {publicStatus}</span></div>
      <div className="tracking-summary"><div><small>Participante</small><strong>{generated ? "Ana Lucía C." : rejected ? "Miguel Ángel S." : correction ? "Sofía Martínez P." : "María Fernanda R."}</strong></div><div><small>Punto de venta</small><strong>{generated ? "Estación La Asociación" : correction ? "Plaza Central" : "Gasolinera Next"}</strong></div><div><small>Monto declarado</small><strong>{generated ? "RD$ 5,000" : rejected ? "RD$ 4,000" : correction ? "RD$ 2,000" : "RD$ 3,000"}</strong></div></div>
      <div className="timeline">
        {timeline.map((item, index) => <div className={item.done ? "timeline-item done" : index === 2 ? `timeline-item current ${rejected ? "current-rejected" : correction ? "current-correction" : ""}` : "timeline-item"} key={item.title}><span className="timeline-dot">{item.done ? <Check /> : rejected && index === 2 ? <X /> : index + 1}</span><div><strong>{item.title}</strong><p>{item.text}</p></div><time>{item.date}</time></div>)}
      </div>
      {generated && <div className="public-tickets"><div className="public-ticket-heading"><div><span>TUS BOLETOS</span><strong>5 boletos activos</strong></div><button onClick={downloadPublicTickets}><DownloadIcon /> Descargar todos</button></div><div className="public-ticket-list">{generatedTokens.map((token) => <div key={token}><span><QRCodeSVG value={`https://rifa-asociacion.do/boleto/${token}`} size={43} level="M" /></span><div><small>TOKEN</small><strong>{token}</strong></div><em>Activo</em></div>)}</div></div>}
      {!generated && <div className={`tracking-note ${rejected ? "rejected-note" : correction ? "correction-note" : ""}`}>{rejected ? <X /> : correction ? <UploadCloud /> : <Clock3 />}<p>{rejected ? <>Motivo: <strong>comprobante utilizado anteriormente.</strong></> : correction ? <>Debes adjuntar nuevamente una imagen donde se distinga el monto.</> : <>Tiempo estimado de validación: <strong>menos de 24 horas</strong>.</>}</p></div>}
    </article>
  );
}

function DownloadIcon() {
  return <ArrowRight size={13} />;
}

function AccessView({ onLogin }: { onLogin: (role: StaffRole) => void }) {
  const [selectedRole, setSelectedRole] = useState<StaffRole>("admin");
  return (
    <section className="access-page">
      <div className="access-art"><div className="access-art-content"><span className="brand-mark large-mark">R</span><span>Panel de gestión</span><h1>Control total.<br />Decisiones claras.</h1><p>Validación, boletos, impresión y auditoría desde un solo lugar.</p></div></div>
      <div className="access-form-wrap">
        <form className="access-form" onSubmit={(event) => event.preventDefault()}>
          <div className="access-heading"><div className="access-lock"><LockKeyhole /></div><span>Acceso autorizado</span><h2>Bienvenido de nuevo</h2><p>Ingresa tus credenciales para acceder al panel.</p></div>
          <label className="field"><span>Usuario o correo</span><input type="email" placeholder="usuario@asociacion.do" /></label>
          <label className="field"><span>Contraseña</span><input type="password" placeholder="••••••••••" /></label>
          <div className="access-options"><label><input type="checkbox" /> Recordarme</label><button type="button">¿Olvidaste tu contraseña?</button></div>
          <div className="demo-access"><span>Vista de demostración</span><div>{(["admin", "validador", "visualizador"] as StaffRole[]).map((role) => <button type="button" key={role} className={selectedRole === role ? "active" : ""} onClick={() => setSelectedRole(role)}>{role === "admin" ? "Administrador" : role === "validador" ? "Validador" : "Visualizador"}</button>)}</div></div>
          <button className="primary-button submit-button" type="button" onClick={() => onLogin(selectedRole)}>Ingresar al panel <ArrowRight size={18} /></button>
          <div className="access-security"><ShieldCheck /><span>Conexión protegida y actividad registrada</span></div>
        </form>
      </div>
    </section>
  );
}
